<zef-dialog
  [open]="state.open"
  [autoFocus]="false"
  [options]="{ restoreFocus: false, maxWidth: '900px', width: '95vw' }"
  (closeTrigger)="onClose$.next()">
  <div class="fx:relative">

    <zef-scrollbar class="__scroll" maxHeight="80vh">
      <div class="__content">

        <!-- Header -->
        <zui-form-section separator="none">
          <span *zuiFormSectionTitle>Configure your ZCP service</span>
          <div *zuiFormSectionDesc>
            A Linux dev container on the <code>base: zcp&#64;1</code> image (Ubuntu),
            deployed into your project's private network. Includes SSHFS access to your
            other services, the Zerops MCP server, <code>zcli</code>, and a curated dev
            toolchain:
          </div>
        </zui-form-section>

        @if (state.formState?.controls) {

          <!-- Base container contents — visually continues the description above -->
          <div class="__preinstalled">
            <div class="__preinstalled-row">
              <span class="__preinstalled-cat">Tools</span>
              <span class="__preinstalled-items"><code>jq</code> <code>yq</code> <code>ripgrep</code> <code>fd</code> <code>fzf</code> <code>bat</code> <code>tree</code> <code>tmux</code> <code>htop</code> <code>ncdu</code> <code>httpie</code> <code>gh</code> <code>make</code> <code>sshfs</code></span>
            </div>
            <div class="__preinstalled-row">
              <span class="__preinstalled-cat">DB clients</span>
              <span class="__preinstalled-items"><code>psql</code> <code>mysql</code> <code>redis-cli</code></span>
            </div>
            <div class="__preinstalled-row">
              <span class="__preinstalled-cat">Browser</span>
              <span class="__preinstalled-items"><code>chrome</code> <code>puppeteer</code> <code>agent-browser</code></span>
            </div>
            <div class="__preinstalled-row">
              <span class="__preinstalled-cat">Shell</span>
              <span class="__preinstalled-items"><code>zsh</code> + Oh My Zsh</span>
            </div>
          </div>

          <p class="__action-hint">
            Pick the AI coding agents and Cloud IDE features below, choose which services
            the container can read and modify, then review the generated
            <code>zerops.yml</code> before deploying.
          </p>

          <!-- §1 Coding Agents (opt-in, default ON) — per-agent picker stack -->
          <div
            class="__toggle-group-wrap  __toggle-group-wrap--cloud-ide"
            [class.is-active]="state.formState?.controls.enableAgent.value === true">

            <zef-form-field
              class="__cloud-ide-toggle"
              last
              [appearance]="'slide-toggle'">
              <mat-slide-toggle
                [ngrxFormControlState]="state.formState?.controls.enableAgent"
                [ngrxEnableFocusTracking]="true">
                <strong>Include Coding Agents</strong>
              </mat-slide-toggle>
            </zef-form-field>

            <p class="__section-desc">
              Install one or more AI coding CLIs inside the container. Run autonomously
              over SSH or interact with them via the Cloud IDE plugin.
            </p>

            @if (state.formState?.controls.enableAgent.value === true) {
              <div class="__toggle-group-wrap_content">

                <div class="__agent-stack">

                  <!-- ── Claude Code row ─────────────────────────────── -->
                  <div
                    class="__agent-row"
                    [class.is-active]="state.formState?.controls.enableAgentClaudeCode.value === true">
                    <label class="__agent-row-head">
                      <mat-checkbox
                        class="__agent-row-checkbox"
                        [ngrxFormControlState]="state.formState?.controls.enableAgentClaudeCode"
                        [ngrxEnableFocusTracking]="true">
                        <span class="__agent-row-meta">
                          <zui-claude-mark class="__agent-row-mark" aria-hidden="true" />
                          <strong>Claude Code</strong>
                          <span class="__agent-row-provider">Anthropic</span>
                        </span>
                      </mat-checkbox>
                    </label>

                    @if (state.formState?.controls.enableAgentClaudeCode.value === true) {
                      <div class="__agent-row-body">
                        <div class="__auth-switcher">
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeClaudeCode.value === 'oauth'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'claude-code', authType: 'oauth' })">
                            <mat-icon fontSet="material-icons-outlined">open_in_browser</mat-icon>
                            Subscription Login
                          </button>
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeClaudeCode.value === 'token'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'claude-code', authType: 'token' })">
                            <mat-icon fontSet="material-icons-outlined">key</mat-icon>
                            API Token
                          </button>
                        </div>

                        @if (state.formState?.controls.authTypeClaudeCode.value === 'token') {
                          <div class="__api-token-field">
                            <zef-form-field>
                              <mat-form-field appearance="outline" color="accent">
                                <mat-label>Anthropic API Key</mat-label>
                                <zui-secret-field>
                                  <input
                                    matInput
                                    type="text"
                                    name="zcp-api-key-claude-code"
                                    autocomplete="off"
                                    spellcheck="false"
                                    data-1p-ignore="true"
                                    data-lpignore="true"
                                    [ngrxEnableFocusTracking]="true"
                                    [ngrxFormControlState]="state.formState?.controls.apiTokenClaudeCode"
                                    placeholder="sk-ant-..." />
                                </zui-secret-field>
                                <mat-hint>Stored as a secret environment variable on the service</mat-hint>
                              </mat-form-field>
                            </zef-form-field>
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <!-- ── Codex row ───────────────────────────────────── -->
                  <div
                    class="__agent-row"
                    [class.is-active]="state.formState?.controls.enableAgentCodex.value === true">
                    <label class="__agent-row-head">
                      <mat-checkbox
                        class="__agent-row-checkbox"
                        [ngrxFormControlState]="state.formState?.controls.enableAgentCodex"
                        [ngrxEnableFocusTracking]="true">
                        <span class="__agent-row-meta">
                          <zui-codex-mark class="__agent-row-mark" aria-hidden="true" />
                          <strong>Codex</strong>
                          <span class="__agent-row-provider">OpenAI</span>
                        </span>
                      </mat-checkbox>
                    </label>

                    @if (state.formState?.controls.enableAgentCodex.value === true) {
                      <div class="__agent-row-body">
                        <div class="__auth-switcher">
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeCodex.value === 'oauth'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'codex', authType: 'oauth' })">
                            <mat-icon fontSet="material-icons-outlined">open_in_browser</mat-icon>
                            ChatGPT Login
                          </button>
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeCodex.value === 'token'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'codex', authType: 'token' })">
                            <mat-icon fontSet="material-icons-outlined">key</mat-icon>
                            API Key
                          </button>
                        </div>

                        @if (state.formState?.controls.authTypeCodex.value === 'token') {
                          <div class="__api-token-field">
                            <zef-form-field>
                              <mat-form-field appearance="outline" color="accent">
                                <mat-label>OpenAI API Key</mat-label>
                                <zui-secret-field>
                                  <input
                                    matInput
                                    type="text"
                                    name="zcp-api-key-codex"
                                    autocomplete="off"
                                    spellcheck="false"
                                    data-1p-ignore="true"
                                    data-lpignore="true"
                                    [ngrxEnableFocusTracking]="true"
                                    [ngrxFormControlState]="state.formState?.controls.apiTokenCodex"
                                    placeholder="sk-..." />
                                </zui-secret-field>
                                <mat-hint>Stored as a secret environment variable on the service</mat-hint>
                              </mat-form-field>
                            </zef-form-field>
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <!-- ── Antigravity row ─────────────────────────────── -->
                  <div
                    class="__agent-row"
                    [class.is-active]="state.formState?.controls.enableAgentAntigravity.value === true">
                    <label class="__agent-row-head">
                      <mat-checkbox
                        class="__agent-row-checkbox"
                        [ngrxFormControlState]="state.formState?.controls.enableAgentAntigravity"
                        [ngrxEnableFocusTracking]="true">
                        <span class="__agent-row-meta">
                          <zui-antigravity-mark class="__agent-row-mark" aria-hidden="true" />
                          <strong>Antigravity</strong>
                          <span class="__agent-row-provider">Google</span>
                        </span>
                      </mat-checkbox>
                    </label>

                    @if (state.formState?.controls.enableAgentAntigravity.value === true) {
                      <div class="__agent-row-body">
                        <div class="__auth-switcher">
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeAntigravity.value === 'oauth'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'antigravity', authType: 'oauth' })">
                            <mat-icon fontSet="material-icons-outlined">open_in_browser</mat-icon>
                            Google Login
                          </button>
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeAntigravity.value === 'token'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'antigravity', authType: 'token' })">
                            <mat-icon fontSet="material-icons-outlined">key</mat-icon>
                            API Key
                          </button>
                        </div>

                        @if (state.formState?.controls.authTypeAntigravity.value === 'token') {
                          <div class="__api-token-field">
                            <zef-form-field>
                              <mat-form-field appearance="outline" color="accent">
                                <mat-label>Google API Key</mat-label>
                                <zui-secret-field>
                                  <input
                                    matInput
                                    type="text"
                                    name="zcp-api-key-antigravity"
                                    autocomplete="off"
                                    spellcheck="false"
                                    data-1p-ignore="true"
                                    data-lpignore="true"
                                    [ngrxEnableFocusTracking]="true"
                                    [ngrxFormControlState]="state.formState?.controls.apiTokenAntigravity"
                                    placeholder="AI..." />
                                </zui-secret-field>
                                <mat-hint>Stored as a secret environment variable on the service</mat-hint>
                              </mat-form-field>
                            </zef-form-field>
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <!-- ── Grok Build row ──────────────────────────────── -->
                  <div
                    class="__agent-row"
                    [class.is-active]="state.formState?.controls.enableAgentGrok.value === true">
                    <label class="__agent-row-head">
                      <mat-checkbox
                        class="__agent-row-checkbox"
                        [ngrxFormControlState]="state.formState?.controls.enableAgentGrok"
                        [ngrxEnableFocusTracking]="true">
                        <span class="__agent-row-meta">
                          <zui-grok-mark class="__agent-row-mark" aria-hidden="true" />
                          <strong>Grok Build</strong>
                          <span class="__agent-row-provider">xAI</span>
                        </span>
                      </mat-checkbox>
                    </label>

                    @if (state.formState?.controls.enableAgentGrok.value === true) {
                      <div class="__agent-row-body">
                        <div class="__auth-switcher">
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeGrok.value === 'oauth'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'grok', authType: 'oauth' })">
                            <mat-icon fontSet="material-icons-outlined">open_in_browser</mat-icon>
                            X Login
                          </button>
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeGrok.value === 'token'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'grok', authType: 'token' })">
                            <mat-icon fontSet="material-icons-outlined">key</mat-icon>
                            API Key
                          </button>
                        </div>

                        @if (state.formState?.controls.authTypeGrok.value === 'token') {
                          <div class="__api-token-field">
                            <zef-form-field>
                              <mat-form-field appearance="outline" color="accent">
                                <mat-label>xAI API Key</mat-label>
                                <zui-secret-field>
                                  <input
                                    matInput
                                    type="text"
                                    name="zcp-api-key-grok"
                                    autocomplete="off"
                                    spellcheck="false"
                                    data-1p-ignore="true"
                                    data-lpignore="true"
                                    [ngrxEnableFocusTracking]="true"
                                    [ngrxFormControlState]="state.formState?.controls.apiTokenGrok"
                                    placeholder="xai-..." />
                                </zui-secret-field>
                                <mat-hint>Stored as a secret environment variable on the service</mat-hint>
                              </mat-form-field>
                            </zef-form-field>
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <!-- ── Cursor CLI row ──────────────────────────────── -->
                  <div
                    class="__agent-row"
                    [class.is-active]="state.formState?.controls.enableAgentCursor.value === true">
                    <label class="__agent-row-head">
                      <mat-checkbox
                        class="__agent-row-checkbox"
                        [ngrxFormControlState]="state.formState?.controls.enableAgentCursor"
                        [ngrxEnableFocusTracking]="true">
                        <span class="__agent-row-meta">
                          <zui-cursor-mark class="__agent-row-mark" aria-hidden="true" />
                          <strong>Cursor CLI</strong>
                          <span class="__agent-row-provider">Cursor</span>
                        </span>
                      </mat-checkbox>
                    </label>

                    @if (state.formState?.controls.enableAgentCursor.value === true) {
                      <div class="__agent-row-body">
                        <div class="__auth-switcher">
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeCursor.value === 'oauth'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'cursor', authType: 'oauth' })">
                            <mat-icon fontSet="material-icons-outlined">open_in_browser</mat-icon>
                            Cursor Login
                          </button>
                          <button
                            type="button"
                            class="__mode-button"
                            [class.is-active]="state.formState?.controls.authTypeCursor.value === 'token'"
                            mat-button
                            (click)="onAuthTypeChange$.next({ agentType: 'cursor', authType: 'token' })">
                            <mat-icon fontSet="material-icons-outlined">key</mat-icon>
                            API Key
                          </button>
                        </div>

                        @if (state.formState?.controls.authTypeCursor.value === 'token') {
                          <div class="__api-token-field">
                            <zef-form-field>
                              <mat-form-field appearance="outline" color="accent">
                                <mat-label>Cursor API Key</mat-label>
                                <zui-secret-field>
                                  <input
                                    matInput
                                    type="text"
                                    name="zcp-api-key-cursor"
                                    autocomplete="off"
                                    spellcheck="false"
                                    data-1p-ignore="true"
                                    data-lpignore="true"
                                    [ngrxEnableFocusTracking]="true"
                                    [ngrxFormControlState]="state.formState?.controls.apiTokenCursor"
                                    placeholder="key_..." />
                                </zui-secret-field>
                                <mat-hint>Stored as a secret environment variable on the service</mat-hint>
                              </mat-form-field>
                            </zef-form-field>
                          </div>
                        }
                      </div>
                    }
                  </div>

                </div>

                <p class="__auth-method-hint">
                  Once the service is active, an authentication prompt will pop up
                  automatically for each agent you sign into via OAuth.
                </p>

              </div>
            }
          </div>

          <zui-separator orientation="vertical" size="medium" spacing="large" position="center" />

          <!-- §2 Cloud IDE (opt-in, default ON) -->
          <div
            class="__toggle-group-wrap  __toggle-group-wrap--cloud-ide"
            [class.is-active]="state.formState?.controls.enableVscode.value === true">

            <zef-form-field
              class="__cloud-ide-toggle"
              last
              [appearance]="'slide-toggle'">
              <mat-slide-toggle
                [ngrxFormControlState]="state.formState?.controls.enableVscode"
                [ngrxEnableFocusTracking]="true">
                <strong>Include Cloud IDE</strong>
              </mat-slide-toggle>
            </zef-form-field>

            <p class="__section-desc">
              Browser-based VS Code with the Claude Code plugin preinstalled — one
              of the easiest ways to work with the agent, alongside terminal, SSH
              and your own IDE over VPN.
            </p>

            @if (state.formState?.controls.enableVscode.value === true) {
              <div class="__toggle-group-wrap_content">

                  <h4 class="__access-method-title">Access Method</h4>

                  <mat-radio-group
                    [ngrxFormControlState]="state.formState?.controls.enablePublicAccess"
                    [ngrxEnableFocusTracking]="true"
                    class="__access-radios">

                    <div
                      class="__access-card"
                      [class.is-active]="state.formState?.controls.enablePublicAccess.value === false">
                      <mat-radio-button [value]="false">
                        <strong>VPN Only</strong>
                        <span>
                          Connect via <code>zcli vpn</code> — a built-in feature of our CLI.
                          Most secure option, no public exposure.
                        </span>
                      </mat-radio-button>
                    </div>

                    <div
                      class="__access-card"
                      [class.is-active]="state.formState?.controls.enablePublicAccess.value === true">
                      <mat-radio-button [value]="true">
                        <strong>Public Access</strong>
                        <span>Browser-accessible with password authentication</span>
                      </mat-radio-button>

                      @if (state.formState?.controls.enablePublicAccess.value === true) {
                        <div class="__password-field">
                          <zef-form-field>
                            <mat-form-field appearance="outline" color="accent">
                              <mat-label>Password</mat-label>
                              <zui-secret-field>
                                <input
                                  matInput
                                  type="text"
                                  name="zcp-vscode-password"
                                  autocomplete="off"
                                  spellcheck="false"
                                  data-1p-ignore="true"
                                  data-lpignore="true"
                                  [ngrxEnableFocusTracking]="true"
                                  [ngrxFormControlState]="state.formState?.controls.vscodePassword" />
                              </zui-secret-field>
                            </mat-form-field>
                          </zef-form-field>
                          <div class="__password-meta">
                            <span class="__password-hint">Auto-generated secure password</span>
                            <button
                              type="button"
                              class="__regen-link"
                              (click)="onRegeneratePassword$.next()">
                              <mat-icon>refresh</mat-icon>
                              <span>Regenerate</span>
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                  </mat-radio-group>
                </div>
              }
            </div>

          <!-- §3 Services - hidden entirely when there's nothing to select -->
          @if ((state.availableServices || []).length > 0) {
            <zui-separator orientation="vertical" size="medium" spacing="large" position="center" />

            <zui-form-section separator="none">
              <span *zuiFormSectionTitle>Connect to Your Services</span>
              <div *zuiFormSectionDesc>
                Select which services the container can read and modify. Mounted via SSHFS at runtime.
              </div>

              <div
                class="__services-grid"
                [class.__services-grid--start]="(state.availableServices || []).length >= 4">
                @for (service of state.availableServices; track $index; let i = $index) {
                  @if (state.formState?.controls?.selectedServices?.controls?.[i]) {
                    <label
                      matRipple
                      class="__service-card"
                      [class.is-active]="state.formState?.controls.selectedServices.controls[i].value === true">
                      <mat-checkbox
                        class="__service-checkbox"
                        [ngrxFormControlState]="state.formState?.controls.selectedServices.controls[i]"
                        [ngrxEnableFocusTracking]="true">
                        <strong class="__service-card_name">@for (part of service.nameParts; track $index) {@if (part.isDev) {<mark class="__service-card_dev">{{ part.text }}</mark>} @else {{{ part.text }}}}</strong>
                        <span class="__service-card_type">{{ service.type }}</span>
                      </mat-checkbox>
                    </label>
                  }
                }
              </div>
            </zui-form-section>
          }

          <!-- §4 Review -->
          <zui-separator orientation="vertical" size="medium" spacing="large" position="center" />

          <zui-form-section separator="none" full>
            <span *zuiFormSectionTitle>Review Deployment Configuration</span>
            <div *zuiFormSectionDesc>
              Generated zerops.yml. Read-only preview.
            </div>

            <zef-form-field>
              <zui-code-field
                class="__yaml-preview"
                [style.--yaml-height.px]="(state.maskedYaml || '').split('\n').length * 19 + 24"
                [value]="state.maskedYaml"
                [editorOptions]="{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  scrollbar: { vertical: 'hidden', horizontal: 'hidden', handleMouseWheel: false },
                  overviewRulerLanes: 0,
                  lineNumbers: 'off',
                  lineNumbersMinChars: 0,
                  lineDecorationsWidth: 16,
                  glyphMargin: false,
                  folding: false,
                  padding: { top: 14, bottom: 14 },
                  quickSuggestions: { other: false, comments: false, strings: false }
                }"
                [editorModel]="{ language: 'yaml', fileName: 'control-plane-config-preview' }">
              </zui-code-field>
            </zef-form-field>
          </zui-form-section>

          <!-- §5 Side-effects warning -->
          <zui-separator orientation="vertical" size="medium" spacing="large" position="center" />

          <zui-form-section separator="none" full>
            <span *zuiFormSectionTitle>Additional Changes On Deploy</span>
            <div *zuiFormSectionDesc>
              These side-effects aren't visible in the yaml above but will be applied so the agent can operate on your project.
            </div>

            <div class="__deploy-warning">
              <mat-icon class="__deploy-warning-icon" fontSet="material-icons-outlined">warning_amber</mat-icon>
              <ul class="__deploy-warning-list">
                <li>An <strong>integration token</strong> scoped to this project with full rights will be created and injected into the container through a secret environment variable.</li>
                <li>The container will be deployed with <code>envIsolation: none</code> and <code>sshIsolation: vpn service&#64;zcp</code> so the agent can read and modify the selected services.</li>
              </ul>
            </div>
          </zui-form-section>
        }

      </div>
    </zef-scrollbar>

    <div class="__buttons">
      @if (state.isRecipeMode) {
        <button
          class="c-button--full"
          color="accent"
          [disabled]="!state.formValid || state.deployLoading"
          mat-flat-button
          type="button"
          (click)="onApplyConfig$.next()">
          Apply Configuration
        </button>
      } @else {
        <zef-progress-error
          full
          [disabled]="!state.formValid || state.deployLoading"
          [key]="actionKey">
          <button
            class="c-button--full"
            color="accent"
            [disabled]="!state.formValid || state.deployLoading"
            mat-raised-button
            type="button"
            (click)="onSubmit$.next()">
            Deploy Control Plane Service
          </button>
        </zef-progress-error>
      }
    </div>

    <button
      type="button"
      class="__dialog-close-button"
      mat-icon-button
      (click)="onClose$.next()">
      <mat-icon>close</mat-icon>
    </button>

  </div>
</zef-dialog>
